import React from "react";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

const AccessV2ErrorCodes = () => {
    const unauthorisedFormat = `{
        "statusCode": 401,
        "message": "You are not authorized.",
        "timestamp": "2023-08-22T14:38:30.097837Z"
      }`;

    const errorFormats = `{
        "statusCode": 422,
        "message": "Access to store information with store id 2011 is not authorized  or store id 2011 is not on-boarded with any of authorized customer.",
        "timestamp": "2023-08-22T14:20:54.1137176Z"
      }`;

    const validationErrorFormat = `{
        "type": "https://tools.ietf.org/html/rfc7231#section-6.5.1",
        "title": "One or more validation errors occurred.",
        "status": 400,
        "traceId": "00-4ba3dec7425ae03d42a14acbe7f3bc98-5dd001e411fbf5c0-00",
        "errors": {
          "StoreId": [
            "The StoreId field is required."
          ]
        }
      }`

    const success = `{
        "terminalId": "20001925",
        "password": "333333"
      }`


    const copyResponse = (text, postfix) => {
        navigator.clipboard.writeText(text);
        var copyBtn = document.getElementById("copy-btn-" + postfix);
        var copyDiv = document.getElementById("copy-message-" + postfix);
        copyBtn.classList.add("hide")
        copyDiv.classList.add("show");
        copyDiv.classList.remove("hide");
        copyBtn.classList.remove("show");
        setTimeout(() => {
            copyDiv.classList.add("hide");
            copyBtn.classList.add("show");
            copyBtn.classList.remove("hide")
            copyDiv.classList.remove("show");
        }, 1500);
    };


    return (
        <article className="docs-article" id="section-8">
            <header className="docs-header">
                <h1 className="docs-heading">Error Handling<span className="docs-time">Last updated: 2023-07-05</span>
                </h1>
                <section className="docs-intro">
                    <p>Westpay offers a REST API for requesting terminal IDs in ECR integration. The API returns JSON responses. To handle responses effectively, clients should validate JSON structure, handle errors using status codes, and implement retries for network issues, enhancing reliability and user experience.</p>
                </section>

                <section className="docs-section" id="error-codes">
                    <h5 className="">200 Success</h5>
                    <p></p>
                    <div className="row">
                        <div className="col-md-11 col-sm-12 col-xs-12">
                            <div className="docs-code-block">
                                <pre className="shadow-lg rounded"><code className="language-dockerfile hljs">
                                    <div className="row code-header">
                                        <div className="col-md-10 languages">
                                            <span>Response</span>
                                        </div>
                                        <div className="col-md-2" onClick={(e) => copyResponse(success, "success")}>
                                            {<span id="copy-btn-success" className="copy-btn show" title="Copy" ><i className="fa fa-copy"></i></span>}
                                            {<div id="copy-message-success" className="copy-message hide">Copied &#x2713;</div>}
                                        </div>
                                    </div>
                                    <div className="code-section">
                                        <SyntaxHighlighter language="json" style={dracula} showLineNumbers>
                                            {success}
                                        </SyntaxHighlighter>
                                    </div>
                                </code></pre>
                            </div>
                        </div>
                    </div>

                    <h5 className="">401 (Unauthorized) Error Response</h5>
                    <p></p>
                    <div className="row">
                        <div className="col-md-11 col-sm-12 col-xs-12">
                            <div className="docs-code-block">
                                <pre className="shadow-lg rounded"><code className="language-dockerfile hljs">
                                    <div className="row code-header">
                                        <div className="col-md-10 languages">
                                            <span>Response</span>
                                        </div>
                                        <div className="col-md-2" onClick={(e) => copyResponse(unauthorisedFormat, "unauthorised-format")}>
                                            {<span id="copy-btn-unauthorised-format" className="copy-btn show" title="Copy"><i className="fa fa-copy"></i></span>}
                                            {<div id="copy-message-unauthorised-format" className="copy-message hide">Copied &#x2713;</div>}
                                        </div>
                                    </div>
                                    <div className="code-section">
                                        <SyntaxHighlighter language="json" style={dracula} showLineNumbers>
                                            {unauthorisedFormat}
                                        </SyntaxHighlighter>
                                    </div>
                                </code></pre>
                            </div>
                        </div>
                    </div>

                    <h5 className="">400 (Validation Error) Response Format</h5>
                    <p></p>
                    <div className="row">
                        <div className="col-md-11 col-sm-12 col-xs-12">
                            <div className="docs-code-block">
                                <pre className="shadow-lg rounded"><code className="language-dockerfile hljs">
                                    <div className="row code-header">
                                        <div className="col-md-10 languages">
                                            <span>Response</span>
                                        </div>
                                        <div className="col-md-2" onClick={(e) => copyResponse(validationErrorFormat, "validation-format")}>
                                            {<span id="copy-btn-validation-format" className="copy-btn show" title="Copy" ><i className="fa fa-copy"></i></span>}
                                            {<div id="copy-message-validation-format" className="copy-message hide">Copied &#x2713;</div>}
                                        </div>
                                    </div>
                                    <div className="code-section">
                                        <SyntaxHighlighter language="json" style={dracula} showLineNumbers>
                                            {validationErrorFormat}
                                        </SyntaxHighlighter>
                                    </div>
                                </code></pre>
                            </div>
                        </div>
                    </div>
                    <h5 className="">Error Response for all other status codes</h5>
                    <p></p>
                    <div className="row">
                        <div className="col-md-11 col-sm-12 col-xs-12">
                            <div className="docs-code-block">
                                <pre className="shadow-lg rounded"><code className="language-dockerfile hljs">
                                    <div className="row code-header">
                                        <div className="col-md-10 languages">
                                            <span>Response</span>
                                        </div>
                                        <div className="col-md-2" onClick={(e) => copyResponse(errorFormats, "error-format")}>
                                            {<span id="copy-btn-error-format" className="copy-btn show" title="Copy" ><i className="fa fa-copy"></i></span>}
                                            {<div id="copy-message-error-format" className="copy-message hide">Copied &#x2713;</div>}
                                        </div>
                                    </div>
                                    <div className="code-section">
                                        <SyntaxHighlighter language="json" style={dracula} showLineNumbers>
                                            {errorFormats}
                                        </SyntaxHighlighter>
                                    </div>
                                </code></pre>
                            </div>
                        </div>
                    </div>
                </section>
            </header>
        </article>
    )
}

export default AccessV2ErrorCodes;